import { useEffect, useState } from 'react'
import { useUserContext } from '@vp/ubik-context'
import { fetchAllViews, fetchDocumentUrls } from '@shared/api/purcs.service'
import type { OrderLineItem } from '@shared/types/order.types'

interface ProductImageResult {
  src: string | undefined
  srcSet: string | undefined
  isLoading: boolean
}

export const useProductImage = (item: OrderLineItem): ProductImageResult => {
  const { locale } = useUserContext()
  const [src, setSrc] = useState<string | undefined>(item.livePreviewUrl || undefined)
  const [srcSet, setSrcSet] = useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(!!item.documentUrl && !!item.productKey)

  useEffect(() => {
    if (!item.documentUrl || !item.productKey) return

    let cancelled = false
    setIsLoading(true)

    const fetchImage = async (): Promise<void> => {
      const docUrls = await fetchDocumentUrls(item.documentUrl!)

      if (cancelled) return

      if (!docUrls?.renderingInstructionsUrl) {
        setIsLoading(false)
        return
      }

      const optionSelections = (item.displayProductAttributes ?? []).reduce<Record<string, string>>((acc, attr) => {
        acc[attr.key] = attr.value
        return acc
      }, {})

      const views = await fetchAllViews({
        culture: locale,
        productKey: item.productKey!,
        productVersion: item.productVersion,
        optionSelections,
        previewInstructionsUri: docUrls.renderingInstructionsUrl,
      })

      if (cancelled) return

      if (views.length > 0 && views[0]) {
        setSrc(views[0].src)
        setSrcSet(views[0].srcSet)
      }

      setIsLoading(false)
    }

    fetchImage()

    return () => {
      cancelled = true
    }
  }, [item.id, item.documentUrl, item.productKey, locale])

  return { src, srcSet, isLoading }
}
